import React, { useEffect, useRef, useState } from 'react'
import * as pdfjsLib from 'pdfjs-dist'

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`

interface PDFField {
  name: string
  x: number
  y: number
  width: number
  height: number
  page: number
}

interface PDFViewerProps {
  pdfUrl: string
  fields: PDFField[]
  onFieldsExtracted: (fields: PDFField[]) => void
  onFieldClick: (fieldName: string) => void
  highlightedFields: string[]
  selectedFields: string[]
}

const PDFViewer: React.FC<PDFViewerProps> = ({
  pdfUrl,
  fields,
  onFieldsExtracted,
  onFieldClick,
  highlightedFields,
  selectedFields
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [pdfDocument, setPdfDocument] = useState<any>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [scale, setScale] = useState(1.7)
  const ongoingRenderRef = useRef<pdfjsLib.RenderTask | null>(null)

  useEffect(() => {
    const loadPDF = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl)
        const pdf = await loadingTask.promise
        setPdfDocument(pdf)
        setTotalPages(pdf.numPages)
        
        // Extract form fields
        const extractedFields: PDFField[] = []
        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
          const page = await pdf.getPage(pageNum)
          const annotations = await page.getAnnotations()
          
          // Only care about a 1:1 viewport for field coordinates
          const viewport = page.getViewport({ scale: 1 })
          
          annotations.forEach((annotation: any) => {
            if (annotation.subtype === 'Widget' && annotation.fieldName) {
              // Returns [x1, y1, x2, y2] in viewport coordinates
              const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(annotation.rect)
              
              extractedFields.push({
                name: annotation.fieldName,
                x: Math.min(x1, x2),
                y: Math.min(y1, y2),
                width: Math.abs(x2 - x1),
                height: Math.abs(y2 - y1),
                page: pageNum
              })
            }
          })
        }
        
        onFieldsExtracted(extractedFields)
      } catch (error) {
        console.error('Error loading PDF:', error)
      }
    }

    if (pdfUrl) {
      loadPDF()
    }
  }, [pdfUrl, onFieldsExtracted])

  useEffect(() => {
    const renderPage = async () => {
      if (!pdfDocument || !canvasRef.current) return

      try {
        const page = await pdfDocument.getPage(currentPage)

        // 1️⃣ Cancel any in-flight render
        if (ongoingRenderRef.current) {
          ongoingRenderRef.current.cancel()
        }

        // 2️⃣ Get a "clean" viewport
        const viewport = page.getViewport({ scale })
        
        const canvas = canvasRef.current
        const context = canvas.getContext('2d')

        if (!context) {
          console.error('Failed to get canvas context')
          return
        }

        // 3️⃣ Reset any stale transform & clear old drawing
        context.setTransform(1, 0, 0, 1, 0, 0)
        context.clearRect(0, 0, canvas.width, canvas.height)

        canvas.height = viewport.height
        canvas.width = viewport.width

        // 4️⃣ Start the new render and remember its task
        const renderContext = {
          canvasContext: context,
          viewport: viewport
        }

        const renderTask = page.render(renderContext)
        ongoingRenderRef.current = renderTask
        await renderTask.promise
      } catch (error) {
        console.error('Error rendering page:', error)
      }
    }

    renderPage()
  }, [pdfDocument, currentPage, scale])

  // Cleanup effect to cancel ongoing renders on unmount
  useEffect(() => {
    return () => {
      if (ongoingRenderRef.current) {
        ongoingRenderRef.current.cancel()
      }
    }
  }, [])

  const handleCanvasClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return

    const rect = canvas.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    // Check if click is on a field
    fields.forEach(field => {
      if (field.page === currentPage) {
        const scaledX = field.x * scale
        const scaledY = field.y * scale
        const scaledWidth = field.width * scale
        const scaledHeight = field.height * scale

        if (x >= scaledX && x <= scaledX + scaledWidth &&
            y >= scaledY && y <= scaledY + scaledHeight) {
          onFieldClick(field.name)
        }
      }
    })
  }

  const getFieldStyle = (field: PDFField) => {
    const isHighlighted = highlightedFields.includes(field.name)
    const isSelected = selectedFields.includes(field.name)
    
    let backgroundColor = 'rgba(255, 255, 0, 0.3)'
    let borderColor = 'rgba(255, 255, 0, 0.8)'
    
    if (isHighlighted) {
      backgroundColor = 'rgba(0, 255, 0, 0.5)'
      borderColor = 'rgba(0, 255, 0, 1.0)'
    }
    
    if (isSelected && !isHighlighted) {
      backgroundColor = 'rgba(0, 0, 255, 0.3)'
      borderColor = 'rgba(0, 0, 255, 0.8)'
    }

    return {
      position: 'absolute' as const,
      left: `${field.x * scale}px`,
      top: `${field.y * scale}px`,
      width: `${field.width * scale}px`,
      height: `${field.height * scale}px`,
      backgroundColor,
      border: `2px solid ${borderColor}`,
      pointerEvents: 'none' as const,
      zIndex: isHighlighted ? 2 : 1
    }
  }

  return (
    <div className="pdf-viewer">
      <div style={{ padding: '7px' }} className="pdf-controls">
        <button 
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage <= 1}
          style={{
            background: '#fff',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '80px',
            transition: 'all 0.2s',
            marginRight: '0.5rem'
          }}
        >
          Previous
        </button>
        <span style={{
          fontSize: '0.875rem',
          color: '#64748b',
          fontWeight: 500,
          margin: '0 0.5rem'
        }}>Page {currentPage} of {totalPages}</span>
        <button 
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage >= totalPages}
          style={{
            background: '#fff',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '80px',
            transition: 'all 0.2s',
            marginRight: '0.5rem'
          }}
        >
          Next
        </button>
        <button 
          onClick={() => setScale(prev => prev + 0.2)}
          style={{
            background: '#fff',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '80px',
            transition: 'all 0.2s',
            marginRight: '0.5rem'
          }}
        >Zoom In</button>
        <button 
          onClick={() => setScale(prev => Math.max(0.5, prev - 0.2))}
          style={{
            background: '#fff',
            border: '1.5px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#1e293b',
            cursor: 'pointer',
            minWidth: '80px',
            transition: 'all 0.2s'
          }}
        >Zoom Out</button>
      </div>
      
      <div className="pdf-container" style={{ position: 'relative', display: 'inline-block' }}>
        <canvas
          ref={canvasRef}
          onClick={handleCanvasClick}
          style={{ cursor: 'pointer', display: 'block', margin: 0, padding: 0 }}
        />
        
        {fields.map((field, index) => 
          field.page === currentPage && (
            <div
              key={`${field.name}-${index}`}
              style={getFieldStyle(field)}
              title={field.name}
            />
          )
        )}
      </div>
    </div>
  )
}

export default PDFViewer 